import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PartnerDealCommitteeEntryResponse,
  PartnerDealCommitteeRoundResponse,
  PartnerDealCommitteeVote,
  PartnerDealCommitteeViewResponse,
} from '@interfaces/partners/responses/partner-deal-committee.response';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerDealCommitteeHttpService } from '@services/http/partners/partner-deal-committee-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { Observable, Subscription, first } from 'rxjs';
import { ModalCommitteeSubmitComponent } from '@app/features/dashboard/commercial/pages/deal-detail/components/modal-committee-submit/modal-committee-submit.component';
import {
  CommitteeFinalVote,
  ModalCommitteeVoteComponent,
} from './modal-committee-vote/modal-committee-vote.component';
import {
  describeQuorum,
  describeReplyResult,
  describeVoteResult,
  entryAuthor,
  entryReference,
  entryRoleLabel,
  entryTag,
  formatEntryNumber,
  isFinalVote,
  isRoundOpen,
  joinNames,
  openContests,
  roundResultTag,
  validateVoteText,
  voterBadge,
  voterSubtitle,
} from './deal-committee-presenter';

/** Tamanho máximo de um texto da discussão, o mesmo do backend. */
const TEXT_MAX_LENGTH = 4000;

/** Rótulo da situação de uma rodada encerrada, para a lista de rodadas anteriores. */
const ROUND_STATUS_LABELS: Record<PartnerDealCommitteeRoundResponse['status'], string> = {
  Open: 'Em votação',
  Approved: 'Aprovado',
  Denied: 'Negado',
  Cancelled: 'Cancelado',
};

/**
 * Painel do comitê de crédito na Visão geral da etapa **Comitê** (card 3789), plugado no
 * `@case ('Comite')` da tela do deal como o painel de Gates.
 *
 * Mostra a votação da rodada (cartões dos votantes, barra de aprovações e negações, aviso de
 * "maioria segura pela contestação"), a discussão imutável (respostas recuadas) e o compositor de
 * acordo com o papel de quem vê: votante (Aprovar, Contestar, Negar, Só comentar), comercial
 * (responder à contestação, comentar) ou só comentário. Sem rodada em votação (deal movido à mão,
 * reaberto, comitê cancelado), oferece o envio ao comitê.
 *
 * Tudo que é regra vem do servidor (`canVote`, `canReply`, `majority`, `isHeldByContest`...); o
 * texto sai do `deal-committee-presenter`. Prazo, regras e material ficam na lateral da tela, no
 * `app-deal-committee-aside`, que lê o mesmo comitê pelo `viewChange`.
 *
 * Só o time interno chega aqui: Comitê é etapa interna, e o servidor recusa usuário de parceiro.
 */
@Component({
  selector: 'app-deal-committee-panel',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    SharedModule,
    ModalCommitteeSubmitComponent,
    ModalCommitteeVoteComponent,
  ],
  templateUrl: './deal-committee-panel.component.html',
  styleUrl: './deal-committee-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealCommitteePanelComponent implements OnChanges {
  private readonly committeeHttp = inject(PartnerDealCommitteeHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  /** Deal exibido. */
  readonly dealId = input.required<string>();
  /** Nome da empresa, para o cabeçalho da modal de envio. */
  readonly companyName = input<string>('');
  /** Muda quando a tela do deal relê pelo tempo real: o painel relê junto, sem a tela de carregamento. */
  readonly reloadKey = input<number>(0);

  /** O comitê como o servidor devolveu, a cada leitura ou ação (a lateral e o próximo passo leem). */
  readonly viewChange = output<PartnerDealCommitteeViewResponse | null>();
  /** O deal mudou junto (decisão, envio): a tela relê etapa, rejeição e linha do tempo. */
  readonly dealChanged = output<void>();

  @ViewChild('submitModal') submitModal: ModalCommitteeSubmitComponent | null = null;
  @ViewChild('voteModal') voteModal: ModalCommitteeVoteComponent | null = null;

  protected readonly view = signal<PartnerDealCommitteeViewResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly draft = signal('');
  protected readonly draftError = signal<string | null>(null);
  /** Ação em curso: trava os botões do compositor. */
  protected readonly sending = signal<PartnerDealCommitteeVote | 'Comment' | 'Reply' | null>(null);
  /** Rodadas anteriores abertas na lista recolhida. */
  protected readonly expandedRounds = signal<ReadonlySet<string>>(new Set());

  protected readonly textMaxLength = TEXT_MAX_LENGTH;
  protected readonly roundStatusLabels = ROUND_STATUS_LABELS;

  /** Rodada exibida como atual. Uma rodada cancelada vai para a lista das anteriores. */
  protected readonly round = computed(() => {
    const current = this.view()?.current ?? null;
    return current && current.status !== 'Cancelled' ? current : null;
  });

  /** Rodadas anteriores, da mais recente para a mais antiga, incluindo a atual cancelada. */
  protected readonly previousRounds = computed(() => {
    const view = this.view();
    if (!view) return [];
    const cancelled = view.current?.status === 'Cancelled' ? [view.current] : [];
    return [...cancelled, ...view.previousRounds].sort((a, b) => b.round - a.round);
  });

  protected readonly isOpen = computed(() => isRoundOpen(this.round()));
  protected readonly myVoter = computed(() => this.round()?.voters.find(v => v.isMe) ?? null);
  protected readonly myVote = computed(() => this.view()?.myVote ?? this.myVoter()?.vote ?? null);
  protected readonly hasFinalVote = computed(() => isFinalVote(this.myVote()));
  protected readonly firstOpenContest = computed(() => openContests(this.round())[0] ?? null);
  /** Aviso do atalho de resposta: quantas contestações esperam e qual o botão responde. */
  protected readonly replyShortcutText = computed(() => {
    const open = openContests(this.round());
    if (!open.length) return '';
    const first = open[0];
    const who = entryAuthor(first);
    return open.length === 1
      ? `A contestação de ${who} (${formatEntryNumber(first.number)}) aguarda resposta.`
      : `${open.length} contestações aguardam resposta; a mais antiga é a de ${who} (${formatEntryNumber(first.number)}).`;
  });
  /** Quem vê tem contestação própria ainda sem resposta (aviso acima do compositor). */
  protected readonly hasMyOpenContest = computed(() =>
    openContests(this.round()).some(e => e.isMine)
  );

  /**
   * Sócio votante que também responde às contestações (enviou o deal ou está na lista da
   * Configuração): escolheu responder em vez de votar. Sem isso o compositor de votante escondia a
   * resposta (E2E de 30/10/2026).
   */
  protected readonly replyMode = signal(false);

  /** Votante que pode responder e tem contestação aberta: mostra o atalho "Responder à contestação". */
  protected readonly canSwitchToReply = computed(
    () => !!this.myVoter() && !!this.view()?.canReply && !!this.firstOpenContest()
  );

  /** Qual compositor aparece embaixo da discussão. */
  protected readonly composer = computed<'voter' | 'voted' | 'submitter' | 'comment' | null>(() => {
    const view = this.view();
    if (!view || !this.isOpen()) return null;
    if (this.replyMode() && this.canSwitchToReply()) return 'submitter';
    if (this.myVoter()) return this.hasFinalVote() ? 'voted' : 'voter';
    if (view.canReply) return 'submitter';
    if (view.canComment) return 'comment';
    return null;
  });

  private loadSubscription?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    const dealChanged = !!changes['dealId'];
    if (dealChanged) {
      this.view.set(null);
      this.draft.set('');
      this.replyMode.set(false);
      this.draftError.set(null);
    }
    this.load(!dealChanged && !!this.view());
  }

  /** Lê o comitê. `silent` mantém o que está na tela enquanto relê (tempo real). */
  protected load(silent = false): void {
    if (!silent) this.loading.set(true);
    this.error.set(null);
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.committeeHttp
      .getCommittee(this.dealId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: view => {
          this.applyView(view);
          this.loading.set(false);
        },
        error: err => {
          this.loading.set(false);
          if (silent && this.view()) return;
          this.error.set(
            err?.errors?.[0]?.message ??
              'Não foi possível carregar o comitê agora. Clique em Tentar de novo.'
          );
        },
      });
  }

  private applyView(view: PartnerDealCommitteeViewResponse | null): void {
    this.view.set(view);
    this.viewChange.emit(view);
  }

  // ─────────────────────────── texto para o template ───────────────────────────

  protected quorum(round: PartnerDealCommitteeRoundResponse): string {
    return describeQuorum(round);
  }

  protected badge = voterBadge;
  protected subtitle = voterSubtitle;
  protected author = entryAuthor;
  protected roleLabel = entryRoleLabel;
  protected reference = entryReference;
  protected entryNumber = formatEntryNumber;

  protected tag(
    entry: PartnerDealCommitteeEntryResponse,
    round: PartnerDealCommitteeRoundResponse
  ) {
    return entryTag(entry, round.id === this.round()?.id);
  }

  /** Iniciais do votante, para o avatar: "Guilherme Araldi" → "GA". */
  protected initials(name: string): string {
    return (name ?? '')
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  /** Largura de um lado da barra, em % dos votantes da rodada. */
  protected barWidth(count: number, round: PartnerDealCommitteeRoundResponse): number {
    const total = round.voters.length;
    return total ? Math.min(100, (count * 100) / total) : 0;
  }

  /** Posição do limiar da maioria na barra do placar, em %. */
  protected majorityMark(round: PartnerDealCommitteeRoundResponse): number {
    return this.barWidth(round.majority, round);
  }

  /** Selo do resultado da rodada encerrada, ao lado do título. */
  protected resultTag(round: PartnerDealCommitteeRoundResponse) {
    return roundResultTag(round);
  }

  /** "Vão votar: A, B e C (lista da Configuração do comitê)." */
  protected configuredVotersText(view: PartnerDealCommitteeViewResponse): string {
    return view.configuredVoterNames.length
      ? `Vão votar: ${joinNames(view.configuredVoterNames)} (lista da Parametrização).`
      : '';
  }

  /** Tom do selo de uma rodada anterior. */
  protected roundTone(round: PartnerDealCommitteeRoundResponse): string {
    if (round.status === 'Approved') return 'success';
    if (round.status === 'Denied') return 'danger';
    return 'neutral';
  }

  /** O que a maioria está decidindo, para o aviso de "maioria segura pela contestação". */
  protected heldDirection(round: PartnerDealCommitteeRoundResponse): string {
    return round.approvals >= round.majority ? 'aprovar' : 'negar';
  }

  protected isExpanded(roundId: string): boolean {
    return this.expandedRounds().has(roundId);
  }

  protected toggleRound(roundId: string): void {
    const next = new Set(this.expandedRounds());
    if (next.has(roundId)) next.delete(roundId);
    else next.add(roundId);
    this.expandedRounds.set(next);
  }

  protected onDraftChange(value: string | null): void {
    this.draft.set(value ?? '');
    this.draftError.set(null);
  }

  // ─────────────────────────── ações ───────────────────────────

  /** Abre a modal de envio ao comitê. */
  protected openSubmit(): void {
    this.submitModal?.open(this.dealId(), this.companyName());
  }

  /** O deal foi enviado ao comitê pela modal. */
  protected onSubmitted(view: PartnerDealCommitteeViewResponse | null): void {
    if (view) this.applyView(view);
    else this.load(true);
    this.dealChanged.emit();
  }

  /** Aprovar ou Negar: valida o texto e pede confirmação com o efeito do voto. */
  protected askFinalVote(vote: CommitteeFinalVote): void {
    const round = this.round();
    if (!round) return;
    const error = validateVoteText(vote, this.draft());
    if (error) {
      this.draftError.set(error);
      return;
    }
    this.voteModal?.open(round, vote, this.draft());
  }

  /** Contestar não pede confirmação: não é voto final. */
  protected contest(): void {
    const error = validateVoteText('Contest', this.draft());
    if (error) {
      this.draftError.set(error);
      return;
    }
    this.sendVote('Contest');
  }

  /** Envia o voto confirmado (ou a contestação). */
  protected sendVote(vote: PartnerDealCommitteeVote): void {
    const text = this.draft().trim();
    this.run(vote, this.committeeHttp.vote(this.dealId(), { vote, text: text || null }), view =>
      describeVoteResult(vote, view?.current)
    );
  }

  /** Comentário na discussão (votante, comercial ou quem tem `COMMENT`). */
  protected comment(): void {
    const text = this.draft().trim();
    if (!text) {
      this.draftError.set('Escreva o comentário antes de enviar.');
      return;
    }
    this.run(
      'Comment',
      this.committeeHttp.comment(this.dealId(), { text }),
      () => 'Comentário registrado na discussão.'
    );
  }

  /** Troca o compositor entre votar e responder à contestação (votante que também responde). */
  protected setReplyMode(on: boolean): void {
    this.replyMode.set(on);
    this.draft.set('');
    this.draftError.set(null);
  }

  /** Resposta do comercial à contestação aberta mais antiga. */
  protected reply(): void {
    const contest = this.firstOpenContest();
    if (!contest) {
      this.comment();
      return;
    }
    const text = this.draft().trim();
    if (!text) {
      this.draftError.set('Escreva a resposta antes de enviar.');
      return;
    }
    this.run(
      'Reply',
      this.committeeHttp.reply(this.dealId(), { contestEntryId: contest.id, text }),
      view => describeReplyResult(view?.current, contest.authorName)
    );
  }

  /**
   * Chama o servidor, aplica o comitê devolvido e avisa a tela quando o deal mudou junto (decisão:
   * Cotação ou Rejeitados). A recusa do servidor vai para o toast com a mensagem dele.
   */
  private run(
    action: PartnerDealCommitteeVote | 'Comment' | 'Reply',
    call: Observable<PartnerDealCommitteeViewResponse | null>,
    successMessage: (view: PartnerDealCommitteeViewResponse | null) => string
  ): void {
    const roundBefore = this.round();
    this.sending.set(action);
    call.pipe(first()).subscribe({
      next: view => {
        this.sending.set(null);
        this.draft.set('');
        this.replyMode.set(false);
        this.draftError.set(null);
        this.toast.showToastSuccess(successMessage(view));
        if (view) this.applyView(view);
        else this.load(true);
        const decided = !!view?.current && view.current.status !== 'Open';
        if (decided && isRoundOpen(roundBefore)) this.dealChanged.emit();
      },
      error: err => {
        this.sending.set(null);
        this.toast.showToastError(
          err?.errors?.[0]?.message ??
            'Não foi possível registrar agora. O texto continua no campo: confira a conexão e envie de novo.'
        );
      },
    });
  }
}
